import { Module } from "vuex";

import { getProjectsApi } from "@/api/ProjectsApi";
import { ALL } from "@/constants";
import { DEFAULT_PROJECTS_PAGE_SIZE } from "@/constants/pagination";
import { setDate } from "@/helpers/dateRangePicker";
import { PlanFilterParams, planListFilters } from "@/settings/userDefaultPlanSettings";
import { RootState } from "@/store";
import { Project } from "@/types/plans";

export interface PlanListState {
  loading: boolean;
  error: string | null;
  nestedProjects: Project[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    next_page: number | null;
  };
}

const initialPagination = {
  page: 1,
  page_size: DEFAULT_PROJECTS_PAGE_SIZE,
  total: 0,
  next_page: null,
};

const initialState: PlanListState = {
  loading: false,
  error: null,
  nestedProjects: [],
  pagination: { ...initialPagination },
};

type Primitive = string | number | boolean;
type EmailObj = { email?: string | null };
type FilterArrayItem = string | EmailObj | null;
type ApiParamValue = Primitive | null | Primitive[];
export type ApiParams = Record<string, ApiParamValue>;

export function transformParamsToApiFormat(params: Partial<PlanFilterParams> = {}): ApiParams {
  const apiParams: ApiParams = {};

  planListFilters.forEach(({ filterKey, apiKey }) => {
    if (filterKey === "dateRange" || filterKey === "lastModified") return;

    const value = params[filterKey as keyof PlanFilterParams];
    let isEmptyArray = false;

    if (Array.isArray(value)) {
      const arr = value as FilterArrayItem[];
      isEmptyArray =
        arr.length === 0 ||
        arr.every((v: FilterArrayItem) => v === null || v === ALL || (typeof v === "object" && v?.email === ALL));
    }

    const isEmptyString = typeof value === "string" && (value.trim() === "" || value === ALL);
    const isEmpty = value === null || isEmptyArray || isEmptyString;

    if (!isEmpty) {
      if (Array.isArray(value)) {
        const arr = value as FilterArrayItem[];
        const hasAll = arr.some((v: FilterArrayItem) => typeof v === "object" && v !== null && v.email === ALL);

        if (!hasAll) {
          const mapped: Primitive[] = arr
            .map((v): Primitive => {
              if (typeof v === "object" && v !== null) return String(v.email ?? "");
              if (v === null) return "";
              return v;
            })
            .filter((x) => x !== "");

          if (mapped.length > 0) {
            apiParams[apiKey] = mapped;
          }
        }
      } else if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        apiParams[apiKey] = value;
      }
    }
  });

  const dateRange = params.dateRange;
  if (dateRange?.type === "basic" && dateRange.value?.startTime && dateRange.value?.endTime) {
    apiParams.publish_date_start = dateRange.value.startTime;
    apiParams.publish_date_end = dateRange.value.endTime;
  } else if (dateRange?.type === "preset" && dateRange.value?.preset) {
    const { startDate, endDate } = setDate(dateRange.value.preset);
    if (startDate && endDate) {
      apiParams.publish_date_start = startDate;
      apiParams.publish_date_end = endDate;
    }
  }

  const lastModified = params.lastModified;
  if (lastModified?.type === "basic" && lastModified.value?.startTime && lastModified.value?.endTime) {
    apiParams.last_modified_start = lastModified.value.startTime;
    apiParams.last_modified_end = lastModified.value.endTime;
  } else if (lastModified?.type === "preset" && lastModified.value?.preset) {
    const { startDate, endDate } = setDate(lastModified.value.preset);
    if (startDate && endDate) {
      apiParams.last_modified_start = startDate;
      apiParams.last_modified_end = endDate;
    }
  }

  if (typeof params.page !== "undefined") {
    apiParams.page = params.page as number;
  }

  if (typeof params.page_size !== "undefined") {
    apiParams.page_size = params.page_size as number;
  }

  return apiParams;
}

const planList: Module<PlanListState, RootState> = {
  namespaced: true,

  state: initialState,

  mutations: {
    setLoading(state, loading: boolean) {
      state.loading = loading;
    },
    setError(state, error: string | null) {
      state.error = error;
    },
    setNestedPlans(state, data: Project[]) {
      state.nestedProjects = data;
    },
    setPagination(state, pagination: PlanListState["pagination"]) {
      state.pagination = pagination;
    },
    resetPagination(state) {
      state.pagination = { ...initialPagination };
    },
  },

  actions: {
    async fetchPlans({ commit }, params: Partial<PlanFilterParams> = {}) {
      commit("setLoading", true);
      commit("setError", null);

      try {
        const transformedParams = transformParamsToApiFormat(params);
        const response = await getProjectsApi().getProjects(transformedParams);

        const projects = response.data;
        const pagination = {
          page: response.page,
          page_size: response.page_size,
          total: response.total,
          next_page: response.next_page,
        };

        commit("setNestedPlans", projects);
        commit("setPagination", pagination);
      } catch (e) {
        console.error("Error fetching plans:", e);
        commit("setError", "Failed to load nested plans");
      } finally {
        commit("setLoading", false);
      }
    },
    clearPlans({ commit }) {
      commit("setNestedPlans", []);
      commit("resetPagination");
    },
  },

  getters: {
    nestedProjects: (state): Project[] => {
      return state.nestedProjects;
    },
    isLoading: (state): boolean => {
      return state.loading;
    },
    hasError: (state): boolean => {
      return !!state.error;
    },
    errorMessage: (state): string | null => {
      return state.error;
    },
    pagination: (state) => {
      return state.pagination;
    },
    currentPage: (state) => state.pagination.page,
    totalPlans: (state) => state.pagination.total,
  },
};

export default planList;
